/** Storage folder tree (desktop): "all files" root, the meme collection, nested folders with rename / new subfolder / delete, inline new top-level folder */

import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import { FolderPlus, HardDrive, Laugh, Loader2 } from 'lucide-react'
import { cn } from '../../lib/utils'
import { useT } from '../../i18n'
import { useStorageFolderTree, useCreateStorageFolder } from '../../hooks/use-storage'
import { cardClass, iconButtonClass } from '../ui/styles'
import { StorageFolderNode } from './storage-folder-node'
import { getFolderPath } from './storage-tree-utils'

interface StorageSidebarProps {
  activeFolderId: string | null
  onSelect: (id: string | null) => void
  /** The meme collection is open (no folder is) */
  memesActive: boolean
  onSelectMemes: () => void
}

const entryClass = (active: boolean) =>
  cn(
    'flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm',
    active ? 'bg-accent-soft font-medium text-accent-text' : 'text-fg-muted hover:bg-surface-hover hover:text-fg',
  )

export function StorageSidebar({ activeFolderId, onSelect, memesActive, onSelectMemes }: StorageSidebarProps) {
  const t = useT()
  const { data, isLoading } = useStorageFolderTree()
  const createFolder = useCreateStorageFolder()
  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState('')

  const folders = useMemo(() => data?.folders ?? [], [data])
  // Folders on the way to the open one start expanded so the tree shows where you are
  const activePath = useMemo(() => new Set(getFolderPath(folders, activeFolderId).map((f) => f.id)), [folders, activeFolderId])

  const handleCreateSubmit = async () => {
    const name = newName.trim()
    setNewName('')
    setCreating(false)
    if (!name) return
    try {
      await createFolder.mutateAsync({ name })
    } catch (err) {
      toast.error(t('storage.error.generic', { message: err instanceof Error ? err.message : String(err) }))
    }
  }

  return (
    <div className={cn(cardClass, 'flex max-h-[calc(100dvh-10rem)] flex-col overflow-hidden')}>
      <div className="flex items-center justify-between border-b border-border py-1 pl-3 pr-1">
        <span className="text-sm font-semibold text-fg">{t('storage.folders')}</span>
        <button
          type="button"
          onClick={() => setCreating(true)}
          className={iconButtonClass}
          aria-label={t('storage.folder.newTopLevel')}
          title={t('storage.folder.newTopLevel')}
        >
          <FolderPlus className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-1.5">
        <button
          type="button"
          onClick={() => onSelect(null)}
          aria-current={activeFolderId === null && !memesActive ? 'page' : undefined}
          className={entryClass(activeFolderId === null && !memesActive)}
        >
          <HardDrive className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span>{t('storage.allFiles')}</span>
        </button>
        <button type="button" onClick={onSelectMemes} aria-current={memesActive ? 'page' : undefined} className={entryClass(memesActive)}>
          <Laugh className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span>{t('storage.memes')}</span>
        </button>

        {isLoading && (
          <div className="flex items-center justify-center py-4" role="status" aria-label={t('common.loading')}>
            <Loader2 className="h-4 w-4 animate-spin text-fg-subtle" aria-hidden="true" />
          </div>
        )}

        <ul aria-label={t('storage.folders')}>
          {folders.map((folder) => (
            <StorageFolderNode
              key={folder.id}
              folder={folder}
              depth={0}
              activeFolderId={activeFolderId}
              activePath={activePath}
              onSelect={onSelect}
            />
          ))}
        </ul>

        {creating && (
          <div className="mt-1 px-1">
            <input
              autoFocus
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onBlur={() => void handleCreateSubmit()}
              onKeyDown={(e) => {
                // Enter submits through onBlur so the folder is created once
                if (e.key === 'Enter') e.currentTarget.blur()
                if (e.key === 'Escape') {
                  setNewName('')
                  setCreating(false)
                }
              }}
              maxLength={200}
              placeholder={t('storage.folder.name')}
              aria-label={t('storage.folder.name')}
              className="w-full rounded-md border border-accent bg-surface px-2 py-1 text-sm text-fg outline-none placeholder:text-fg-subtle"
            />
          </div>
        )}
      </div>
    </div>
  )
}
